import type { AssetDataExtractionRunHandle, AssetTaskStatus, ParsedAsset } from '@aikoa/common'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAsset } from '~/data/assets/useAssets'
import { trpc } from '~/lib/trpc'
import { useReaction } from '~/lib/useReaction.ts'

const isAssetDataComplete = (asset?: ParsedAsset | null) => !!asset?.data && !!asset.address

export function usePendingAsset(props?: {
  assetId?: string
  onSuccess?: (asset: ParsedAsset) => unknown
  onFailure?: (asset?: ParsedAsset) => unknown
  onInitialFailure?: (asset?: ParsedAsset) => unknown
}) {
  const { assetId: assetIdParam, onSuccess, onFailure, onInitialFailure } = props ?? {}
  const [currentAssetId, setCurrentAssetId] = useState<string | undefined>(assetIdParam)

  const assetId = useMemo(() => currentAssetId || assetIdParam, [currentAssetId, assetIdParam])

  const {
    data: taskStatuses,
    refetch: refetchTaskStatuses,
    isLoading: isInitialTaskLoading,
  } = useQuery(
    trpc.property.getPropertyTaskStatuses.queryOptions(
      { assetId: assetId ?? '' },
      { enabled: !!assetId }
    )
  )

  const initialTaskStatus: AssetDataExtractionRunHandle | null | undefined = taskStatuses?.initial

  const [currentRunId, setCurrentRunId] = useState<string | null>(initialTaskStatus?.runId ?? null)

  useEffect(() => {
    if (!currentRunId && initialTaskStatus?.runId) {
      setCurrentRunId(initialTaskStatus.runId)
    }
  }, [initialTaskStatus, currentRunId])

  const { mutateAsync: addAsset, isPending: isAddingAsset } = useMutation(
    trpc.property.addFromSource.mutationOptions({
      onSuccess: (data) => {
        if (data) {
          setCurrentRunId(data.runId ?? null)
          setCurrentAssetId(data.assetId)
        }
      },
    })
  )

  const { data: runStatus } = useQuery(
    trpc.tasks.getRunStatus.queryOptions(
      { runId: currentRunId ?? '' },
      {
        enabled: !!currentRunId,
        refetchInterval: (query) =>
          query.state.data === 'PENDING' || currentRunId !== initialTaskStatus?.runId
            ? 2000
            : false,
        refetchIntervalInBackground: true,
        initialData: initialTaskStatus?.status,
      }
    )
  )

  const currentRunStatus = useMemo(
    () => runStatus ?? initialTaskStatus?.status,
    [runStatus, initialTaskStatus]
  )

  const {
    asset,
    isLoading: isAssetLoading,
    refetch: refetchAsset,
  } = useAsset(
    assetId ?? '',
    currentRunStatus === 'PENDING'
      ? {
          pollingInterval: 2000,
          shouldPoll: (data) => !isAssetDataComplete(data as ParsedAsset | null | undefined),
        }
      : undefined
  )

  const { mutate: retry, isPending: isRetrying } = useMutation(
    trpc.property.retryPropertyExtraction.mutationOptions({
      onSuccess: (data: { runId: string } | null) => {
        if (data?.runId) {
          setCurrentRunId(data.runId)
        }

        void refetchTaskStatuses()
      },
    })
  )

  const retryExtraction = useCallback(() => {
    if (assetId) {
      retry({ assetId })
    }
  }, [assetId, retry])

  const refetchAssetData = useCallback(async () => {
    await refetchAsset()
    await refetchTaskStatuses()
  }, [refetchAsset, refetchTaskStatuses])

  const isMutationPending = isAddingAsset || isRetrying
  const isDataFetching = isAssetLoading || isInitialTaskLoading

  const isAssetComplete = useMemo(() => isAssetDataComplete(asset), [asset])

  const taskStatus = useMemo((): AssetTaskStatus => {
    if (!assetId) {
      return 'UNKNOWN'
    }

    if (isAssetComplete) {
      return 'COMPLETED'
    }

    return currentRunStatus ?? 'UNKNOWN'
  }, [assetId, isAssetComplete, currentRunStatus])

  const processingType = useMemo(() => {
    if (isAddingAsset) {
      return 'ADDING'
    }

    if (isRetrying) {
      return 'RETRYING'
    }

    return false
  }, [isAddingAsset, isRetrying])

  useReaction(
    currentRunStatus === 'COMPLETED' && !isAssetDataComplete(asset) && !!assetId,
    useCallback(() => {
      const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

      return (async () => {
        await delay(300)
        await refetchAssetData()

        if (!isAssetDataComplete(asset)) {
          await delay(700)
          await refetchAssetData()
        }
      })()
    }, [refetchAssetData, asset])
  )

  useReaction(
    taskStatus === 'COMPLETED' && isAssetDataComplete(asset),
    useCallback(
      () => refetchAsset().then(({ data }) => onSuccess?.(data!)),
      [onSuccess, refetchAsset]
    )
  )

  const [lastFailureRunId, setLastFailureRunId] = useState<string | null>(null)

  useReaction(
    (taskStatus === 'FAILED' &&
      !isAssetLoading &&
      !isAssetDataComplete(asset) &&
      !!currentRunId &&
      currentRunId !== lastFailureRunId) ||
      false,
    useCallback(() => {
      setLastFailureRunId(currentRunId ?? null)
      const handler =
        currentRunId === initialTaskStatus?.runId ? (onInitialFailure ?? onFailure) : onFailure
      handler?.(asset ?? undefined)
    }, [asset, currentRunId, initialTaskStatus?.runId, onFailure, onInitialFailure])
  )

  return {
    asset,
    taskStatus,
    isAssetComplete,
    processingType,
    addAsset,
    retryExtraction,
    isMutationPending,
    isDataFetching,
    refetchAsset: refetchAssetData,
    assetId,
    setAssetId: setCurrentAssetId,
  }
}
